import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { t, type Locale, type TranslateKey } from "./i18n.js";
import shikkaLogo from "./assets/shikka-logo.png";
import { applyBrandMode, resolveBrandMode, getInitialBrandMode, type BrandMode } from "@smart-shikka/react";

export const SHIKKA_LOGO = shikkaLogo;

export interface LocaleCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  tr: (key: TranslateKey) => string;
}

const LocaleContext = createContext<LocaleCtx | null>(null);

export function LocaleProvider({ locale, setLocale, children }: { locale: Locale; setLocale: (l: Locale) => void; children: ReactNode }) {
  const value: LocaleCtx = { locale, setLocale, tr: (key) => t(key, locale) };
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleCtx {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}

/* ---------- Brand theme (light / dark / system), shared with desktop ---------- */
export type Theme = BrandMode;

interface ThemeCtx {
  theme: Theme;
  resolved: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => getInitialBrandMode());
  const resolved = resolveBrandMode(theme);

  useEffect(() => {
    applyBrandMode(theme);
  }, [theme]);

  useEffect(() => {
    if (theme !== "system" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => applyBrandMode("system");
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, [theme]);

  const toggle = () => setTheme((p) => (resolveBrandMode(p) === "dark" ? "light" : "dark"));
  const value: ThemeCtx = { theme, resolved, setTheme, toggle };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

/* ---------- Icons (self-hosted SVG, no remote fonts) ---------- */
export function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}
export function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
export function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
export function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><path d="M1 1l22 22" />
    </svg>
  );
}

/** Password input with a show/hide eye toggle (mirrors desktop). */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  minLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  minLength?: number;
}) {
  const { tr } = useLocale();
  const [show, setShow] = useState(false);
  return (
    <div className="ss-field">
      <label htmlFor={id}>{label}</label>
      <div className="pw-wrap">
        <input
          id={id}
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          minLength={minLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
        />
        <button
          type="button"
          className="ss-icon-btn"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? tr("hidePassword") : tr("showPassword")}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button type="button" className="ss-icon-btn" onClick={toggle} aria-label="Toggle theme">
      {theme === "dark" ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

export function LocaleToggle() {
  const { locale, setLocale } = useLocale();
  return (
    <div className="ss-toggle" role="group" aria-label="language">
      <button type="button" className={locale === "en" ? "active" : ""} onClick={() => setLocale("en")}>
        EN
      </button>
      <button type="button" className={locale === "hi" ? "active" : ""} onClick={() => setLocale("hi")}>
        हिं
      </button>
    </div>
  );
}

/** Small brand lockup (topbar / cards). */
export function Brand() {
  const { tr } = useLocale();
  return (
    <div className="brand">
      <img className="logo" src={shikkaLogo} alt="Smart Shikka" />
      <div>
        <h1>{tr("appName")}</h1>
        <small>{tr("tagline")}</small>
      </div>
    </div>
  );
}

/** Big hero brand used on the split login layout (mirrors desktop). */
export function HeroBrand() {
  const { tr, locale } = useLocale();
  return (
    <div className="ss-login-hero">
      <div className="mascot"><img src={shikkaLogo} alt="Shikka" /></div>
      <h1>{tr("appName")}</h1>
      <div className="tag">{tr("tagline")}</div>
      <span className="ss-pill"><span className="dot" />{locale === "hi" ? "मालिक की अनुमति से चलता है" : "runs with owner approval"}</span>
    </div>
  );
}
